import axios from 'axios'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { UnableToConnectToNetsuiteToastMessage } from 'src/app/Toast/Toast'
import { OrganizationConnectedPlatform } from 'src/enums'
import { OrganizationConnectionIntentListModel } from 'src/models/organizationConnectionIntent.models'
import { OrganizationIntegrationListResponse } from 'src/models/organizationIntegration.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { CreateOrgIntegrationRequest } from 'src/requests/createOrgIntegration.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const CREATE_ORG_CONNECTION_INTENT_INIT =
  'app/CREATE_ORG_CONNECTION_INTENT_INIT'
export const CREATE_ORG_CONNECTION_INTENT_SUCCESS =
  'app/CREATE_ORG_CONNECTION_INTENT_SUCCESS'
export const CREATE_ORG_CONNECTION_INTENT_FAILURE =
  'app/CREATE_ORG_CONNECTION_INTENT_FAILURE'

export type CreateOrgConnectionIntentSuccessPayload = {
  connectionIntent: OrganizationConnectionIntentListModel
}

export type CreateOrgConnectionIntentFailurePayload =
  BackendErrorPayload<string>

export interface CreateOrgConnectionIntentInitAction {
  type: typeof CREATE_ORG_CONNECTION_INTENT_INIT
}
export interface CreateOrgConnectionIntentSuccessAction {
  type: typeof CREATE_ORG_CONNECTION_INTENT_SUCCESS
  payload: CreateOrgConnectionIntentSuccessPayload
}
export interface CreateOrgConnectionIntentFailureAction {
  type: typeof CREATE_ORG_CONNECTION_INTENT_FAILURE
  payload: CreateOrgConnectionIntentFailurePayload
}

export type CreateOrgConnectionIntentActions =
  | CreateOrgConnectionIntentInitAction
  | CreateOrgConnectionIntentSuccessAction
  | CreateOrgConnectionIntentFailureAction

const createCreateOrgConnectionIntentInitAction =
  (): CreateOrgConnectionIntentInitAction => ({
    type: CREATE_ORG_CONNECTION_INTENT_INIT,
  })
const createCreateOrgConnectionIntentSuccessAction = (
  payload: CreateOrgConnectionIntentSuccessPayload,
): CreateOrgConnectionIntentSuccessAction => ({
  type: CREATE_ORG_CONNECTION_INTENT_SUCCESS,
  payload,
})
const createCreateOrgConnectionIntentFailureAction = (
  payload: CreateOrgConnectionIntentFailurePayload,
): CreateOrgConnectionIntentFailureAction => ({
  type: CREATE_ORG_CONNECTION_INTENT_FAILURE,
  payload,
})

export const createOrgConnectionIntent = (
  orgId: string,
  integrationType?: OrganizationConnectedPlatform,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  CreateOrgConnectionIntentActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, CreateOrgConnectionIntentActions>,
    getState: () => RootState,
  ): Promise<void> => {
    let type
    switch (integrationType) {
      case OrganizationConnectedPlatform.NETSUITE:
        type = 'NETSUITE'
        break
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
      case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
        type = 'QUICKBOOKS'
        break
    }

    dispatch(createCreateOrgConnectionIntentInitAction())
    return axios
      .post<CreateOrgConnectionIntentSuccessPayload>(
        `/organizations/${orgId}/connectionintents`,
        {
          type: type,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createCreateOrgConnectionIntentSuccessAction(response.data))
        },
        (err) => {
          if (err.response?.status !== 402) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <UnableToConnectToNetsuiteToastMessage />,
                failureColorScheme: true,
                hideImage: true,
                isDarkBackground: true,
              }),
            )
          }
          dispatch(
            createCreateOrgConnectionIntentFailureAction(err.response.data),
          )
        },
      )
  }
}

export const CREATE_ORG_INTEGRATION_INIT = 'app/CREATE_ORG_INTEGRATION_INIT'
export const CREATE_ORG_INTEGRATION_SUCCESS =
  'app/CREATE_ORG_INTEGRATION_SUCCESS'
export const CREATE_ORG_INTEGRATION_FAILURE =
  'app/CREATE_ORG_INTEGRATION_FAILURE'

export type CreateOrgIntegrationSuccessPayload = {
  organizationConnection: OrganizationIntegrationListResponse
}

export type CreateOrgIntegrationFailurePayload = BackendErrorPayload<string>

export interface CreateOrgIntegrationInitAction {
  type: typeof CREATE_ORG_INTEGRATION_INIT
}
export interface CreateOrgIntegrationSuccessAction {
  type: typeof CREATE_ORG_INTEGRATION_SUCCESS
  payload: CreateOrgIntegrationSuccessPayload
}
export interface CreateOrgIntegrationFailureAction {
  type: typeof CREATE_ORG_INTEGRATION_FAILURE
  payload: CreateOrgIntegrationFailurePayload
}

export type CreateOrgIntegrationActions =
  | CreateOrgIntegrationInitAction
  | CreateOrgIntegrationSuccessAction
  | CreateOrgIntegrationFailureAction

const createCreateOrgIntegrationInitAction =
  (): CreateOrgIntegrationInitAction => ({
    type: CREATE_ORG_INTEGRATION_INIT,
  })
const createCreateOrgIntegrationSuccessAction = (
  payload: CreateOrgIntegrationSuccessPayload,
): CreateOrgIntegrationSuccessAction => ({
  type: CREATE_ORG_INTEGRATION_SUCCESS,
  payload,
})
const createCreateOrgIntegrationFailureAction = (
  payload: CreateOrgIntegrationFailurePayload,
): CreateOrgIntegrationFailureAction => ({
  type: CREATE_ORG_INTEGRATION_FAILURE,
  payload,
})

export const createOrgIntegration = (
  orgId: string,
  body: Partial<CreateOrgIntegrationRequest>,
): ThunkAction<Promise<void>, RootState, void, CreateOrgIntegrationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, CreateOrgIntegrationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createCreateOrgIntegrationInitAction())
    return axios
      .post<CreateOrgIntegrationSuccessPayload>(
        `/organizations/${orgId}/connections`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createCreateOrgIntegrationSuccessAction(response.data))
        },
        (err) => {
          dispatch(createCreateOrgIntegrationFailureAction(err.response.data))
        },
      )
  }
}
